import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { FaEdit, FaExternalLinkAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { getPressCoverage, savePressCoverage, deletePressCoverage, readPressImage } from '../../services/pressCoverageService';
import './PressCoverage.scss';

const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const blank = () => ({ title: '', sourceName: '', sourceUrl: '', publishedAt: today(), summary: '', isActive: false, removePicture: false });
const externalUrl = value => {
  try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : null; } catch { return null; }
};
const imageUrl = value => value?.startsWith('/BasinResimleri/') ? `https://iskenderun.bel.tr${value}` : '';
const errorMessage = err => err.response?.data?.resultMessage || err.message || 'İşlem tamamlanamadı.';

export default function PressCoverage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ search: '', isActive: '' });
  const [draftSearch, setDraftSearch] = useState('');
  const [draftStatus, setDraftStatus] = useState('');
  const [result, setResult] = useState({ items: [], totalCount: 0, pageCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [revision, setRevision] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [formError, setFormError] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (!file) { setPreview(''); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    getPressCoverage({ page, pageSize: 20, search: filters.search, isActive: filters.isActive || undefined }, controller.signal)
      .then(data => {
        if (controller.signal.aborted) return;
        if (page > Math.max(1, data.pageCount)) setPage(Math.max(1, data.pageCount));
        setResult(data);
      })
      .catch(err => { if (!controller.signal.aborted) setError(errorMessage(err)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [page, filters, revision]);

  const openForm = (item = null) => {
    setForm(item ? { ...item, publishedAt: item.publishedAt.slice(0, 10), summary: item.summary || '', removePicture: false } : blank());
    setFile(null);
    setPreview('');
    setFormError('');
    setShowForm(true);
  };
  const change = event => setForm(previous => ({ ...previous, [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value }));
  const chooseFile = event => {
    const chosen = event.target.files?.[0];
    if (!chosen) { setFile(null); return; }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(chosen.type) || chosen.size > 5 * 1024 * 1024) {
      setFormError('En fazla 5 MB boyutunda JPG, PNG veya WebP görsel seçin.');
      event.target.value = '';
      setFile(null);
      return;
    }
    setFormError('');
    setFile(chosen);
    setForm(previous => ({ ...previous, removePicture: false }));
  };
  const save = async event => {
    event.preventDefault();
    if (busy) return;
    if (!form.title.trim() || !form.sourceName.trim() || !externalUrl(form.sourceUrl.trim())) {
      setFormError('Başlık, yayın kuruluşu ve geçerli bir http/https haber bağlantısı girin.');
      return;
    }
    setBusy(true);
    setFormError('');
    setSuccess('');
    try {
      const pictureBase64 = file ? await readPressImage(file) : undefined;
      await savePressCoverage({ ...form, title: form.title.trim(), sourceName: form.sourceName.trim(), sourceUrl: form.sourceUrl.trim(), pictureBase64 });
      setShowForm(false);
      setFile(null);
      setRevision(n => n + 1);
      setSuccess('Basında Biz kaydı kaydedildi.');
    } catch (err) { setFormError(errorMessage(err)); } finally { setBusy(false); }
  };
  const toggle = async item => {
    setBusy(true); setError(''); setSuccess('');
    try {
      await savePressCoverage({ ...item, isActive: !item.isActive });
      setRevision(n => n + 1);
      setSuccess(item.isActive ? 'Haber yayından kaldırıldı.' : 'Haber yayına alındı.');
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!deleting || busy) return;
    setBusy(true); setError(''); setSuccess('');
    try {
      await deletePressCoverage(deleting.guid);
      setDeleting(null);
      setRevision(n => n + 1);
      setSuccess('Kayıt kaldırıldı.');
    } catch (err) { setDeleting(null); setError(errorMessage(err)); } finally { setBusy(false); }
  };
  const currentPreview = preview || (!form.removePicture ? imageUrl(form.pictureUrl) : '');

  return (
    <div className="press-admin">
      <div className="press-admin__heading"><div><h1>Basında Biz</h1><p>Basında yayımlanan haberleri ve kaynak bağlantılarını yönetin.</p></div><Button onClick={() => openForm()} disabled={busy}><FaPlus /> Yeni haber</Button></div>
      {error && <Alert variant="danger">{error} <Button variant="link" onClick={() => setRevision(n => n + 1)}>Tekrar dene</Button></Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}
      <Card><Card.Body>
        <Form className="press-admin__filters" onSubmit={event => { event.preventDefault(); setPage(1); setFilters({ search: draftSearch.trim(), isActive: draftStatus }); }}>
          <Form.Group controlId="press-search"><Form.Label>Başlık veya yayın kuruluşu</Form.Label><Form.Control type="search" maxLength={200} value={draftSearch} onChange={e => setDraftSearch(e.target.value)} placeholder="Basın haberlerinde ara" /></Form.Group>
          <Form.Group controlId="press-status"><Form.Label>Yayın durumu</Form.Label><Form.Select value={draftStatus} onChange={e => setDraftStatus(e.target.value)}><option value="">Tümü</option><option value="true">Yayında</option><option value="false">Taslak</option></Form.Select></Form.Group>
          <Button type="submit" disabled={loading}>Filtrele</Button>
          <Button variant="outline-secondary" onClick={() => { setDraftSearch(''); setDraftStatus(''); setPage(1); setFilters({ search: '', isActive: '' }); }}>Temizle</Button>
        </Form>
        {loading ? <div className="press-admin__loading" role="status"><Spinner animation="border" size="sm" /> Kayıtlar yükleniyor...</div> : !error && <>
          <p className="text-muted">{result.totalCount} kayıt</p>
          <Table responsive hover className="press-admin__table"><thead><tr><th>Haber</th><th>Yayın kuruluşu</th><th>Tarih</th><th>Durum</th><th>İşlemler</th></tr></thead><tbody>
            {!result.items.length ? <tr><td colSpan={5} className="text-center py-4">Kayıt bulunamadı.</td></tr> : result.items.map(item => <tr key={item.guid}>
              <td><strong>{item.title}</strong>{externalUrl(item.sourceUrl) && <a className="d-block" href={externalUrl(item.sourceUrl)} target="_blank" rel="noopener noreferrer">Kaynak haber <FaExternalLinkAlt aria-label="Yeni sekmede açılır" /></a>}</td>
              <td>{item.sourceName}</td><td>{new Date(item.publishedAt).toLocaleDateString('tr-TR')}</td><td><Badge bg={item.isActive ? 'success' : 'secondary'}>{item.isActive ? 'Yayında' : 'Taslak'}</Badge></td>
              <td><div className="press-admin__actions"><Button size="sm" variant="outline-primary" disabled={busy} onClick={() => openForm(item)}><FaEdit /> Düzenle</Button><Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => toggle(item)}>{item.isActive ? 'Yayından kaldır' : 'Yayına al'}</Button><Button size="sm" variant="outline-danger" disabled={busy} onClick={() => setDeleting(item)}><FaTrash /> Sil</Button></div></td>
            </tr>)}
          </tbody></Table>
          {result.pageCount > 1 && <nav className="press-admin__pagination" aria-label="Basında Biz sayfalama"><Button variant="outline-secondary" disabled={page <= 1 || busy} onClick={() => setPage(n => n - 1)}>Önceki</Button><span>{page} / {result.pageCount}</span><Button variant="outline-secondary" disabled={page >= result.pageCount || busy} onClick={() => setPage(n => n + 1)}>Sonraki</Button></nav>}
        </>}
      </Card.Body></Card>

      <Modal show={showForm} onHide={() => { if (!busy) setShowForm(false); }} size="lg" backdrop="static" keyboard={!busy}>
        <Form onSubmit={save}><Modal.Header closeButton={!busy}><Modal.Title>{form.guid ? 'Basın haberini düzenle' : 'Basın haberi ekle'}</Modal.Title></Modal.Header><Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <fieldset disabled={busy} className="press-admin__form">
            <Form.Group controlId="press-title"><Form.Label>Başlık *</Form.Label><Form.Control name="title" required maxLength={250} value={form.title} onChange={change} /></Form.Group>
            <Form.Group controlId="press-source"><Form.Label>Yayın kuruluşu *</Form.Label><Form.Control name="sourceName" required maxLength={200} value={form.sourceName} onChange={change} placeholder="Gazete, haber sitesi veya TV kanalı" /></Form.Group>
            <Form.Group controlId="press-url"><Form.Label>Kaynak haber bağlantısı *</Form.Label><Form.Control type="url" name="sourceUrl" required maxLength={2048} value={form.sourceUrl} onChange={change} placeholder="https://..." /></Form.Group>
            <Form.Group controlId="press-date"><Form.Label>Yayın tarihi *</Form.Label><Form.Control type="date" name="publishedAt" required min="1900-01-01" value={form.publishedAt} onChange={change} /></Form.Group>
            <Form.Group controlId="press-summary"><Form.Label>Kısa açıklama</Form.Label><Form.Control as="textarea" rows={3} name="summary" maxLength={1000} value={form.summary} onChange={change} /><Form.Text>{form.summary.length}/1000 karakter</Form.Text></Form.Group>
            <Form.Group controlId="press-image"><Form.Label>Kapak görseli</Form.Label><Form.Control type="file" accept="image/jpeg,image/png,image/webp" onChange={chooseFile} /><Form.Text>JPG, PNG veya WebP; en fazla 5 MB. Yeni görseller sunucuda WebP olarak kaydedilir.</Form.Text></Form.Group>
            {currentPreview && <img className="press-admin__preview" src={currentPreview} alt="Kapak görseli önizlemesi" />}
            {form.pictureUrl && !file && <Form.Check id="press-remove-image" label="Mevcut görseli kaldır" name="removePicture" checked={form.removePicture} onChange={change} />}
            <Form.Check id="press-is-active" label="Web sitesinde yayımla" name="isActive" checked={form.isActive} onChange={change} />
          </fieldset>
        </Modal.Body><Modal.Footer><Button variant="secondary" disabled={busy} onClick={() => setShowForm(false)}>Vazgeç</Button><Button type="submit" disabled={busy}>{busy ? 'Kaydediliyor...' : 'Kaydet'}</Button></Modal.Footer></Form>
      </Modal>
      <Modal show={!!deleting} onHide={() => { if (!busy) setDeleting(null); }} backdrop="static"><Modal.Header closeButton={!busy}><Modal.Title>Haberi kaldır</Modal.Title></Modal.Header><Modal.Body>“{deleting?.title}” kaydı listelerden kaldırılacak. Devam etmek istiyor musunuz?</Modal.Body><Modal.Footer><Button variant="secondary" disabled={busy} onClick={() => setDeleting(null)}>Vazgeç</Button><Button variant="danger" disabled={busy} onClick={remove}>{busy ? 'Kaldırılıyor...' : 'Kaldır'}</Button></Modal.Footer></Modal>
    </div>
  );
}
